import React, { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { Send, Sparkles, Bot, Brain, Target, TrendingUp, AlertTriangle, ShoppingBag, Users, Lightbulb, RefreshCw, Play, Loader2, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const STARTERS = [
  "What are my top 3 revenue opportunities this week?",
  "Which content drove the most paid conversions?",
  "Suggest a launch plan for my new $99 course.",
  "Which HOT leads should I personally follow up with?",
];

export default function Copilot() {
  const [tab, setTab] = useState("strategy");          // strategy | chat
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "I'm Ashra Copilot — your AI business strategist. Ask me anything about your revenue, leads, or what to do next." },
  ]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const sessionId = useRef(`copilot-${Date.now()}`);
  const listRef = useRef(null);

  useEffect(() => { listRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [msgs]);

  const send = async (content) => {
    const payload = content ?? text;
    if (!payload.trim()) return;
    setMsgs((m) => [...m, { role: "user", text: payload }]);
    setText("");
    setLoading(true);
    try {
      const { data } = await api.post("/copilot/chat", { text: payload, session_id: sessionId.current });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I had trouble. Try again." }]);
    }
    setLoading(false);
  };

  const generateBrief = async () => {
    setBriefLoading(true);
    try {
      const { data } = await api.post("/copilot/strategize");
      setBrief(data);
    } catch {}
    setBriefLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="copilot-page">
      <div className="px-4 sm:px-6 lg:px-10 py-5 border-b border-line bg-white/60 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl ashra-gradient-bg grid place-items-center shadow-sm"><Bot className="h-5 w-5 text-white" /></div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-bold text-ink">Ashra Copilot</h1>
            <p className="text-xs text-meta">Your AI revenue strategist · GPT-4o · grounded on your real data</p>
          </div>
        </div>
        <div className="mt-4 inline-flex rounded-full bg-zinc-100 p-1">
          <button onClick={() => setTab("strategy")} data-testid="tab-strategy"
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition focus-ring ${tab === "strategy" ? "ashra-gradient-bg text-white shadow-sm" : "text-meta hover:text-ink"}`}>
            <Brain className="inline h-3 w-3 mr-1" /> Strategy Brief
          </button>
          <button onClick={() => setTab("chat")} data-testid="tab-chat"
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition focus-ring ${tab === "chat" ? "ashra-gradient-bg text-white shadow-sm" : "text-meta hover:text-ink"}`}>
            <Sparkles className="inline h-3 w-3 mr-1" /> Chat
          </button>
        </div>
      </div>

      {tab === "strategy" ? (
        <StrategyView brief={brief} loading={briefLoading} generate={generateBrief} />
      ) : (
        <ChatView msgs={msgs} text={text} setText={setText} send={send} loading={loading} listRef={listRef} />
      )}
    </div>
  );
}

function StrategyView({ brief, loading, generate }) {
  if (!brief) {
    return (
      <div className="flex-1 grid place-items-center px-6 py-10">
        <div className="max-w-md text-center">
          <div className="h-16 w-16 rounded-3xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 grid place-items-center mx-auto mb-5 shadow-lg shadow-violet-500/30">
            <Brain className="h-8 w-8 text-white" />
          </div>
          <h2 className="font-display text-2xl font-bold text-ink mb-2">Generate your AI strategy brief</h2>
          <p className="text-sm text-meta mb-6">Ashra Strategist analyzes your real workspace data — leads, funnels, content, automations — and produces a structured brief with predicted revenue, bottlenecks, top opportunities, upsells, churn risks, pricing & campaign ideas.</p>
          <button onClick={generate} disabled={loading} data-testid="generate-brief-btn"
            className="rounded-full ashra-gradient-bg px-6 py-3 text-sm font-bold text-white inline-flex items-center gap-2 shadow-md hover:shadow-lg disabled:opacity-60 transition focus-ring">
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Strategizing…" : "Generate brief"}
          </button>
        </div>
      </div>
    );
  }

  const b = brief.brief || {};
  const Section = ({ icon: Icon, title, color, children }) => (
    <div className="surface p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className={`h-8 w-8 rounded-xl grid place-items-center ${color}`}><Icon className="h-4 w-4" /></div>
        <h3 className="font-display text-sm font-bold text-ink">{title}</h3>
      </div>
      {children}
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin px-4 sm:px-6 lg:px-10 py-6 max-w-6xl w-full mx-auto">
      <div className="rounded-3xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 p-[1px] mb-5">
        <div className="rounded-3xl bg-white p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">Executive summary</p>
              <p className="mt-2 text-base lg:text-lg text-ink-soft leading-relaxed">{b.executive_summary || "—"}</p>
            </div>
            <button onClick={generate} disabled={loading} className="text-xs font-medium rounded-full border border-line bg-white px-3 py-1.5 text-meta hover:text-ink hover:bg-zinc-50 inline-flex items-center gap-1 focus-ring">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Regenerate
            </button>
          </div>
          {b.predicted_revenue_30d != null && (
            <div className="mt-4 flex items-end gap-3 flex-wrap">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">Predicted revenue (30d)</p>
                <p className="font-display text-4xl font-extrabold text-ink mt-1 ai-gradient-text-strong">${Math.round(b.predicted_revenue_30d).toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        {b.funnel_bottleneck && (
          <Section icon={AlertTriangle} title="Funnel bottleneck" color="bg-warning-soft text-warning">
            <p className="text-sm text-ink"><span className="font-bold">Stage:</span> {b.funnel_bottleneck.stage}</p>
            <p className="text-sm text-ink-soft mt-1"><span className="font-semibold text-meta">Why:</span> {b.funnel_bottleneck.reason}</p>
            <p className="text-sm text-ai mt-2 font-semibold"><Lightbulb className="inline h-3.5 w-3.5 mr-1" />{b.funnel_bottleneck.fix}</p>
          </Section>
        )}
        {b.top_opportunities?.length > 0 && (
          <Section icon={Target} title="Top opportunities" color="bg-ai-soft text-ai">
            <ul className="space-y-2">
              {b.top_opportunities.map((o, i) => (
                <li key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`strategist-opp-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink">{o.title}</p>
                    <p className="text-xs text-meta">{o.action}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {o.impact_usd ? <span className="text-success font-bold">+${Math.round(o.impact_usd).toLocaleString()}</span> : null}
                    <StrategistRun kind="opportunity" payload={{ title: o.title, action: o.action, impact_usd: o.impact_usd }} testid={`strategist-opp-run-${i}`} label="Track" />
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        {b.upsell_recommendations?.length > 0 && (
          <Section icon={ShoppingBag} title="Upsell plays" color="bg-success-soft text-success">
            <ul className="space-y-2">
              {b.upsell_recommendations.map((u, i) => (
                <li key={i} className="text-sm">
                  <p className="font-semibold text-ink">{u.product} <span className="text-xs text-meta font-normal">→ {u.audience}</span></p>
                  {u.expected_uplift_pct != null && <p className="text-xs text-success font-medium">+{u.expected_uplift_pct}% expected uplift</p>}
                </li>
              ))}
            </ul>
          </Section>
        )}
        {b.churn_risks?.length > 0 && (
          <Section icon={AlertTriangle} title="Churn risks" color="bg-danger-soft text-danger">
            <ul className="space-y-2">
              {b.churn_risks.map((c, i) => (
                <li key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`strategist-churn-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-ink font-medium">{c.signal}</p>
                    <p className="text-xs text-ai mt-0.5">{c.recommendation}</p>
                  </div>
                  <StrategistRun kind="churn" payload={{ signal: c.signal, recommendation: c.recommendation }} testid={`strategist-churn-run-${i}`} label="Defuse" />
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        {b.recovery_plays?.length > 0 && (
          <Section icon={RefreshCw} title="Revenue recovery plays" color="bg-warning-soft text-warning">
            <ul className="space-y-2">
              {b.recovery_plays.map((r, i) => {
                const validKinds = ["abandoned_checkout", "inactive_warm", "failed_payment", "no_response", "webinar_no_buy"];
                const kind = validKinds.includes(r.kind) ? r.kind : null;
                return (
                  <li key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`recovery-play-${kind || "generic"}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink">{r.play}</p>
                      <p className="text-xs text-meta">{r.target}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.projected_recovery_usd ? <span className="text-success font-bold">+${Math.round(r.projected_recovery_usd).toLocaleString()}</span> : null}
                      {kind && <StrategistRun kind="recovery" payload={{ play: r.play, target: r.target, kind, projected_recovery_usd: r.projected_recovery_usd }} testid={`recovery-play-run-${kind}`} />}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}
        {b.pricing_suggestions?.length > 0 && (
          <Section icon={TrendingUp} title="Pricing suggestions" color="bg-info-soft text-info">
            <ul className="space-y-2">
              {b.pricing_suggestions.map((p, i) => (
                <li key={i} className="text-sm">
                  <p className="font-semibold text-ink">{p.product}</p>
                  <p className="text-xs text-ink-soft">{p.suggestion}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        {b.lead_priority_list?.length > 0 && (
          <Section icon={Users} title="Personally follow up with" color="bg-ai-soft text-ai">
            <ul className="space-y-2">
              {b.lead_priority_list.map((l, i) => (
                <li key={i} className="text-sm">
                  <p className="font-semibold text-ink">{l.name}</p>
                  <p className="text-xs text-meta">{l.why}</p>
                  <p className="text-xs text-ai mt-0.5">→ {l.action}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {b.campaign_ideas?.length > 0 && (
          <Section icon={Lightbulb} title="Campaign ideas" color="bg-warning-soft text-warning">
            <ul className="space-y-2">
              {b.campaign_ideas.map((c, i) => (
                <li key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`strategist-campaign-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink">{c.name} <span className="text-xs text-meta font-normal">· {c.channel}</span></p>
                    <p className="text-xs text-ink-soft mt-0.5 italic">"{c.hook}"</p>
                  </div>
                  <StrategistRun kind="campaign" payload={{ name: c.name, hook: c.hook, channel: c.channel }} testid={`strategist-campaign-run-${i}`} label="Draft" />
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {b.content_priorities?.length > 0 && (
          <Section icon={Sparkles} title="Content priorities" color="bg-ai-soft text-ai">
            <ul className="space-y-2">
              {b.content_priorities.map((c, i) => (
                <li key={i} className="text-sm">
                  <p className="font-semibold text-ink">{c.content_type}: {c.angle}</p>
                  <p className="text-xs text-meta">{c.reason}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {b.recommended_automations?.length > 0 && (
          <Section icon={Bot} title="Recommended automations" color="bg-success-soft text-success">
            <ul className="space-y-2">
              {b.recommended_automations.map((a, i) => (
                <li key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`strategist-auto-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink">{a.automation}</p>
                    <p className="text-xs text-meta">{a.why}</p>
                  </div>
                  <StrategistRun kind="automation" payload={{ automation: a.automation, why: a.why }} testid={`strategist-auto-run-${i}`} label="Add" />
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {b.raw && !b.executive_summary && (
        <div className="surface p-5 mt-4">
          <p className="text-sm text-meta mb-2 font-semibold">Raw response</p>
          <pre className="text-xs text-ink-soft whitespace-pre-wrap">{b.raw}</pre>
        </div>
      )}
    </div>
  );
}

function StrategistRun({ kind, payload, testid, label = "Run" }) {
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/strategist/run/${kind}`, payload);
      if (r.data?.status === "queued") toast.success(`${kind.replace(/_/g, " ")} queued`);
      else if (r.data?.status === "done") toast.success(`${kind.replace(/_/g, " ")} created`);
      else toast.success("Action recorded");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run");
    }
    setBusy(false);
  };
  return (
    <button onClick={run} disabled={busy || done} data-testid={testid}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 shrink-0 transition focus-ring ${
        done ? "bg-success-soft text-success" : "bg-ai-soft text-ai hover:bg-violet-100"
      } disabled:opacity-60`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : done ? <Check className="h-3 w-3" /> : <Play className="h-3 w-3" />}
      {done ? "Done" : label}
    </button>
  );
}



function ChatView({ msgs, text, setText, send, loading, listRef }) {
  return (
    <>
      <div ref={listRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 sm:px-6 lg:px-10 py-6 space-y-4 max-w-3xl w-full mx-auto">
        {msgs.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "bubble-creator" : "bubble-ai text-ink-soft"
            }`}>
              {m.text}
            </div>
          </motion.div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bubble-ai inline-flex items-center gap-2 text-sm text-ink-soft px-4 py-3">
              <Sparkles className="h-4 w-4 text-ai animate-pulse" /> Thinking…
            </div>
          </div>
        )}
      </div>

      {msgs.length <= 1 && (
        <div className="px-4 sm:px-6 lg:px-10 pb-4 max-w-3xl w-full mx-auto grid sm:grid-cols-2 gap-2">
          {STARTERS.map((s) => (
            <button key={s} onClick={() => send(s)} className="text-left rounded-2xl border border-line bg-white hover:bg-zinc-50 hover:border-violet-300 px-4 py-3 text-sm text-ink-soft transition focus-ring" data-testid={`starter-${s.slice(0,10)}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-line px-4 sm:px-6 lg:px-10 py-4 bg-white">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ask Copilot about revenue, leads, content…"
            className="flex-1 rounded-full bg-zinc-50 border border-line px-4 sm:px-5 py-3 text-sm text-ink placeholder:text-meta-soft outline-none focus:border-violet-500 focus:bg-white transition"
            data-testid="copilot-input"
          />
          <button disabled={loading} className="rounded-full ashra-gradient-bg px-4 sm:px-5 py-3 text-sm text-white font-semibold inline-flex items-center gap-1 disabled:opacity-50 shadow-sm hover:shadow-md transition focus-ring min-w-[44px] min-h-[44px] justify-center" data-testid="copilot-send">
            <Send className="h-4 w-4" /> <span className="hidden sm:inline">Send</span>
          </button>
        </div>
      </form>
    </>
  );
}
